import React, { useRef, ReactNode } from 'react';
import { Box, Fade } from '@mui/material';
import { useFullscreen } from './useFullscreen';
import { useAutoHideControls } from './useAutoHideControls';
import AlertOverlay from './AlertOverlay';
import { VideoWallAlert } from '../../types/canvision.types';

interface FullscreenContainerProps {
  children: ReactNode;
  controls: ReactNode;
  isPlaying: boolean;
  onMouseMove?: () => void;
  // Alert overlay props
  alerts?: VideoWallAlert[];
  onViewAlert?: (alert: VideoWallAlert) => void;
  onDismissAlert?: (alertId: number) => void;
  alertsVisible?: boolean;
}

function FullscreenContainer({
  children,
  controls,
  isPlaying,
  onMouseMove,
  alerts = [],
  onViewAlert,
  onDismissAlert,
  alertsVisible = true,
}: FullscreenContainerProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  const {
    isFullscreen,
    isSupported: isFullscreenSupported,
    toggleFullscreen,
  } = useFullscreen(containerRef);

  const { isVisible: controlsVisible, showControls } = useAutoHideControls({
    timeout: 3000,
    enabled: isFullscreen,
    alwaysShowWhenPaused: true,
    isPaused: !isPlaying,
  });

  // Handle mouse movement to show controls
  const handleMouseMove = () => {
    if (isFullscreen) {
      showControls();
    }
    onMouseMove?.();
  };

  // Pass fullscreen state and toggle function to controls via cloning
  const controlsWithFullscreen = React.isValidElement(controls)
    ? React.cloneElement(controls as React.ReactElement<any>, {
        isFullscreen,
        isFullscreenSupported,
        onToggleFullscreen: toggleFullscreen,
      })
    : controls;

  return (
    <Box
      ref={containerRef}
      onMouseMove={handleMouseMove}
      sx={{
        position: 'relative',
        width: '100%',
        height: isFullscreen ? '100vh' : 'auto',
        flex: isFullscreen ? undefined : 1,
        bgcolor: isFullscreen ? 'black' : 'transparent',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Camera grid area */}
      <Box
        sx={{
          flex: 1,
          overflow: isFullscreen ? 'auto' : 'visible',
          p: isFullscreen ? 2 : 0,
          position: 'relative',
        }}
      >
        {children}

        {/* Alert overlay */}
        {alertsVisible && alerts.length > 0 && onViewAlert && onDismissAlert && (
          <AlertOverlay
            alerts={alerts}
            onView={onViewAlert}
            onDismiss={onDismissAlert}
            position="bottom-right"
            maxVisible={3}
          />
        )}
      </Box>

      {/* Controls bar */}
      {isFullscreen ? (
        <Fade in={controlsVisible}>
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              bgcolor: 'grey.900',
              opacity: 0.9,
              p: 1,
              '& .MuiPaper-root': {
                bgcolor: 'transparent',
                boxShadow: 'none',
                mb: 0,
                p: 1,
              },
            }}
          >
            {controlsWithFullscreen}
          </Box>
        </Fade>
      ) : (
        controlsWithFullscreen
      )}
    </Box>
  );
}

export default FullscreenContainer;
